import {
  LanguageAbbreviation,
  RtlLanguageAbbreviations,
} from '@/constants/common';
import storage from '@/utils/authorization-util';
import dayjs from 'dayjs';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { upperFirst } from 'lodash';
import { initReactI18next } from 'react-i18next';
import translation_en from './en';
import translation_fa from './fa';

// DOCFLOW keeps UI language under a rebranded key so leftover RAGFlow
// `lng=en` values do not keep English after the Persian default ships.
export const LANGUAGE_STORAGE_KEY = 'docflow.lng';

const languageImports: Record<string, () => Promise<{ default: any }>> = {
  [LanguageAbbreviation.En]: () => import('./en'),
  [LanguageAbbreviation.Zh]: () => import('./zh'),
  [LanguageAbbreviation.ZhTraditional]: () => import('./zh-traditional'),
  [LanguageAbbreviation.Id]: () => import('./id'),
  [LanguageAbbreviation.Ja]: () => import('./ja'),
  [LanguageAbbreviation.Es]: () => import('./es'),
  [LanguageAbbreviation.Vi]: () => import('./vi'),
  [LanguageAbbreviation.Ru]: () => import('./ru'),
  [LanguageAbbreviation.PtBr]: () => import('./pt-br'),
  [LanguageAbbreviation.De]: () => import('./de'),
  [LanguageAbbreviation.Fr]: () => import('./fr'),
  [LanguageAbbreviation.It]: () => import('./it'),
  [LanguageAbbreviation.Bg]: () => import('./bg'),
  [LanguageAbbreviation.Ar]: () => import('./ar'),
  [LanguageAbbreviation.Fa]: () => import('./fa'),
  [LanguageAbbreviation.Tr]: () => import('./tr'),
  [LanguageAbbreviation.Ko]: () => import('./ko'),
};

const supportedLanguageCodes: Intl.UnicodeBCP47LocaleIdentifier[] =
  Object.keys(languageImports);

export const supportedLanguages = supportedLanguageCodes.map((code) => {
  const locale = new Intl.Locale(code);

  return {
    code,
    locale,
    displayName: upperFirst(
      new Intl.DisplayNames(locale, { type: 'language' }).of(code)!,
    ),
  };
});

export const DEFAULT_LANGUAGE_CODE =
  import.meta.env.VITE_DEFAULT_LANGUAGE_CODE || LanguageAbbreviation.Fa;

const resources = {
  [LanguageAbbreviation.En]: translation_en,
  // Eager-load Persian so the default language paints correctly on first frame.
  [LanguageAbbreviation.Fa]: translation_fa,
};

const dayjsLocaleFor = (lng: string): string => {
  if (lng === 'zh' || lng.startsWith('zh')) {
    return 'zh-cn';
  }
  if (lng === LanguageAbbreviation.Fa || lng.startsWith('fa')) {
    return 'fa';
  }
  if (lng === LanguageAbbreviation.Ar || lng.startsWith('ar')) {
    return 'ar';
  }
  return lng;
};

export const isRtlLanguage = (lng: string): boolean => {
  const base = (lng || '').split('-')[0];
  return (
    RtlLanguageAbbreviations.has(lng) || RtlLanguageAbbreviations.has(base)
  );
};

const updateDocumentLocale = (lng: string) => {
  document.documentElement.lang = lng;
  document.documentElement.dir = isRtlLanguage(lng) ? 'rtl' : 'ltr';
  dayjs.locale(dayjsLocaleFor(lng));
};

const resolveInitialLanguage = (): string => {
  const stored = storage.getLanguage()?.trim();
  if (stored && supportedLanguageCodes.includes(stored)) {
    return stored;
  }
  return DEFAULT_LANGUAGE_CODE;
};

i18n
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    detection: {
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      order: ['localStorage'],
      caches: [],
    },
    supportedLngs: supportedLanguageCodes,
    resources,
    lng: resolveInitialLanguage(),
    fallbackLng: DEFAULT_LANGUAGE_CODE,
    interpolation: {
      escapeValue: false,
    },
  });

export const loadLanguageAsync = async (lng: string): Promise<void> => {
  const normalizedLng = lng;

  if (i18n.hasResourceBundle(normalizedLng, 'translation')) {
    return;
  }

  const importFn = languageImports[normalizedLng];
  if (!importFn) {
    console.warn(`Language ${lng} is not supported for lazy loading`);
    return;
  }

  try {
    const module = await importFn();
    const translationData = module.default?.translation || module.default;
    i18n.addResourceBundle(normalizedLng, 'translation', translationData);
  } catch (error) {
    console.error(`Failed to load language ${lng}:`, error);
  }
};

export const changeLanguageAsync = async (
  lng: string,
  options: { persist?: boolean } = {},
): Promise<void> => {
  const { persist = true } = options;
  const normalizedLng = lng;

  if (
    normalizedLng !== LanguageAbbreviation.En &&
    normalizedLng !== LanguageAbbreviation.Fa &&
    !i18n.hasResourceBundle(normalizedLng, 'translation')
  ) {
    await loadLanguageAsync(normalizedLng);
  }

  if (persist) {
    storage.setLanguage(lng);
  }

  updateDocumentLocale(lng);

  await i18n.changeLanguage(normalizedLng);
};

export const initLanguage = async (): Promise<void> => {
  const currentLng = resolveInitialLanguage();
  await changeLanguageAsync(currentLng);
};

export default i18n;
